// M142 Deel A — /vlogs galerij.
// - Grid met thumbnails per vlog (3 kolommen desktop / 1 mobiel).
// - Knop "Nieuwe vlog maken" met vriendelijke wacht-modal als de
//   Luma-key nog niet is geactiveerd.
// - Lege staat met korte uitleg + CTA.
// - Banner: "JustEasySEO maakt de vlog, jij plaatst 'm zelf online."
import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { useEmbed } from "@/contexts/EmbedContext";
import api from "@/lib/api";
import {
  Video, Loader2, Download, Plus, Trash2, Sparkles, X, ExternalLink,
} from "lucide-react";

function StatusBadge({ status }) {
  const map = {
    klaar: { cls: "border-pos-green/40 text-pos-green bg-pos-green/10", label: "Klaar" },
    in_wacht: { cls: "border-warn-amber/40 text-warn-amber bg-warn-amber/5", label: "In de wacht" },
    aan_het_maken: { cls: "border-accent-teal/40 text-accent-teal bg-accent-teal/10", label: "Aan het maken" },
    mislukt: { cls: "border-neg-red/40 text-neg-red bg-neg-red/10", label: "Mislukt" },
  };
  const s = map[status] || map.in_wacht;
  return <span className={`text-[10px] px-2 py-0.5 rounded border ${s.cls}`}>{s.label}</span>;
}

function WaitModal({ onClose, message }) {
  return (
    <div data-testid="vlog-wait-modal"
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full rounded-card border border-border-hairline bg-bg-panel p-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-warn-amber" />
            <div className="text-sm font-display text-text-primary">Bijna klaar voor gebruik</div>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-text-primary leading-relaxed">{message}</p>
        <div className="mt-4 flex justify-end">
          <button onClick={onClose} data-testid="vlog-wait-modal-close"
                   className="text-[11px] px-3 py-1.5 rounded border border-accent-teal/40 text-accent-teal hover:bg-accent-teal/10">
            Begrepen
          </button>
        </div>
      </div>
    </div>
  );
}

export default function VlogsPage() {
  const { hideInnerHeader } = useEmbed();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [waitMsg, setWaitMsg] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    try { const r = await api.get("/vlogs/gallery"); setData(r.data); }
    catch (_e) { setData({ items: [], luma_key_present: false }); }
  };
  useEffect(() => { load(); }, []);

  const startGenerate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/vlogs/generate", { topic_nl: "Nieuwe merkvlog" });
      if (r.data.status === "wacht_op_activatie") {
        setWaitMsg(r.data.message_nl);
      } else {
        await load();
      }
    } catch (_e) {
      setWaitMsg("Er ging iets mis. Probeer het straks opnieuw.");
    } finally { setBusy(false); }
  };

  const deleteVlog = (id) => {
    setConfirmDel(id);
  };
  const confirmDelete = async () => {
    const id = confirmDel;
    setConfirmDel(null);
    try { await api.delete(`/vlogs/${id}`); await load(); }
    catch (_e) {}
  };

  const download = async (id, title) => {
    try {
      const r = await api.get(`/vlogs/gallery/${id}/download`);
      const url = r.data.download_url;
      if (url) window.open(url, "_blank", "noopener");
    } catch (_e) {}
  };

  const items = data?.items || [];

  return (
    <AppLayout>
      {!hideInnerHeader && (
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-text-primary tracking-tight flex items-center gap-2"
                 data-testid="vlogs-title">
              <Video className="w-5 h-5 text-accent-teal" /> Vlog-galerij
            </h1>
            <p className="text-sm text-text-muted mt-1">
              Alle vlogs die JustEasySEO voor jou heeft gemaakt. Download en plaats zelf.
            </p>
          </div>
        </div>
      )}
      <div className="mb-4 flex justify-end">
        <button onClick={startGenerate} disabled={busy}
                 data-testid="vlogs-new-btn"
                 className="inline-flex items-center gap-1.5 rounded-[10px] px-4 py-2 bg-accent-teal text-white text-sm font-medium hover:bg-accent-teal-dim disabled:opacity-50"
                 style={{ boxShadow: "0 1px 3px rgba(8,127,140,0.24)" }}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          Nieuwe vlog maken
        </button>
      </div>

      <div data-testid="vlogs-banner"
            className="rounded-card border border-accent-teal/30 bg-accent-teal/5 px-4 py-3 mb-5">
        <div className="text-sm text-accent-teal font-semibold">
          JustEasySEO maakt de vlog, jij plaatst 'm zelf online.
        </div>
        <div className="text-[11px] text-text-muted mt-0.5">
          Zo houd je zelf controle over je socials.
        </div>
      </div>

      {!data ? (
        <div className="text-text-muted text-sm flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Vlogs laden…
        </div>
      ) : items.length === 0 ? (
        <div data-testid="vlogs-empty"
              className="rounded-card border border-dashed border-border-hairline bg-bg-panel p-8 text-center">
          <Video className="w-8 h-8 text-text-muted mx-auto mb-3" />
          <div className="text-sm font-display text-text-primary mb-2">Nog geen vlogs</div>
          <p className="text-xs text-text-muted max-w-md mx-auto mb-4 leading-relaxed">
            Een korte vlog laat jouw werk zien in beweging — vaak sterker dan tekst of foto's.
            Laat JustEasySEO er eentje maken om te zien wat het kan opleveren.
          </p>
          <button onClick={startGenerate} disabled={busy}
                   data-testid="vlogs-empty-cta"
                   className="text-[11px] px-3 py-1.5 rounded border border-accent-teal/40 text-accent-teal hover:bg-accent-teal/10">
            Maak mijn eerste vlog
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
              data-testid="vlogs-grid">
          {items.map((v, i) => (
            <div key={v.vlog_id} data-testid={`vlog-card-${i}`}
                  className="rounded-card border border-border-hairline bg-bg-panel overflow-hidden">
              <div className="aspect-video bg-bg-canvas relative">
                {v.thumbnail_url ? (
                  <img src={v.thumbnail_url} alt=""
                        className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-text-subtle">
                    <Video className="w-10 h-10" />
                  </div>
                )}
                <div className="absolute top-2 right-2"><StatusBadge status={v.status} /></div>
              </div>
              <div className="p-3 space-y-2">
                <div className="text-sm text-text-primary font-medium truncate" title={v.title}>{v.title}</div>
                <div className="text-[11px] text-text-muted flex gap-3">
                  <span>{v.duration_seconds}s</span>
                  {v.created_at && (
                    <span>{new Date(v.created_at).toLocaleDateString("nl-NL", { day:"2-digit", month:"short" })}</span>
                  )}
                </div>
                {v.publishing_hint && (
                  <div className="text-[11px] text-text-muted italic">{v.publishing_hint}</div>
                )}
                <div className="flex gap-2 pt-1">
                  <button onClick={() => download(v.vlog_id, v.title)}
                           disabled={v.status !== "klaar"}
                           data-testid={`vlog-download-${i}`}
                           className="flex-1 text-[11px] px-3 py-1.5 rounded border border-accent-teal/40 text-accent-teal hover:bg-accent-teal/10 flex items-center justify-center gap-1 disabled:opacity-40">
                    <Download className="w-3 h-3" /> Download
                  </button>
                  <button onClick={() => deleteVlog(v.vlog_id)}
                           data-testid={`vlog-delete-${i}`}
                           className="text-[11px] px-2 py-1.5 rounded border border-border-hairline text-text-muted hover:text-neg-red hover:border-neg-red/40">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {waitMsg && <WaitModal message={waitMsg} onClose={() => setWaitMsg(null)} />}
      {confirmDel && (
        <div data-testid="vlog-confirm-delete"
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full rounded-card border border-border-hairline bg-bg-panel p-5">
            <div className="text-sm font-display text-text-primary mb-2">Vlog verwijderen?</div>
            <p className="text-xs text-text-primary leading-relaxed mb-4">
              Weet je het zeker? Deze vlog wordt verwijderd uit jouw galerij. Je kunt 'm daarna opnieuw laten maken.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)} data-testid="vlog-confirm-cancel"
                       className="text-[11px] px-3 py-1.5 rounded border border-border-hairline text-text-muted">
                Annuleren
              </button>
              <button onClick={confirmDelete} data-testid="vlog-confirm-ok"
                       className="text-[11px] px-3 py-1.5 rounded border border-neg-red/40 text-neg-red hover:bg-neg-red/10">
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
